import React, { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api';
import { useT } from '../lib/i18n';

/**
 * ChatCompanion — a quiet floating ∞ button that opens a multi-turn
 * reflective dialog grounded in the book's framework.
 *
 * The button itself is small, copper-edged, and stays out of the way.
 * The panel slides up from the bottom on mobile, anchors bottom-right on desktop.
 */
export default function ChatCompanion() {
  const { t, lang, dir } = useT();
  const [open, setOpen] = useState(false);
  const [history, setHistory] = useState([]);     // [{role, content}]
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const scrollerRef = useRef(null);
  const inputRef = useRef(null);

  // Lazy-load history when the panel opens for the first time per session.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/chat/history');
        if (cancelled) return;
        // Backfill stable ids on hydrated history so `key` references survive renders.
        const msgs = (r.data?.messages || []).map((m, i) => ({ ...m, id: m.id || `h-${i}` }));
        setHistory(msgs);
      } catch (e) {
        // Empty history is the expected fresh state — log only if it's an unexpected error.
        if (e?.response?.status && e.response.status !== 404) {
          console.error('ChatCompanion history load failed:', e);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Auto-scroll to bottom when new messages arrive.
  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [history, open, sending]);

  // Focus input when opening.
  useEffect(() => {
    if (open && inputRef.current) {
      const id = setTimeout(() => inputRef.current?.focus(), 200);
      return () => clearTimeout(id);
    }
  }, [open]);

  // Escape closes.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setError('');
    setSending(true);
    // Optimistic user message — id is a monotonically-rising timestamp so React
    // never re-keys an existing bubble even if messages briefly share content.
    const userMsg = { id: `u-${Date.now()}`, role: 'user', content: text };
    setHistory(h => [...h, userMsg]);
    setInput('');
    try {
      const r = await api.post('/chat', { message: text, language: lang });
      const reply = r.data?.reply || '';
      setHistory(h => [...h, { id: `a-${Date.now()}`, role: 'assistant', content: reply }]);
    } catch (e) {
      console.error('ChatCompanion.send failed:', e);
      setError(t('chat.error'));
      // Roll back the optimistic message — user can retry from input that was kept.
      setHistory(h => h.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const clearAll = async () => {
    try {
      await api.delete('/chat/history');
      setHistory([]);
      setError('');
    } catch (e) {
      // Non-blocking: UI keeps the local view if the server call fails.
      console.error('ChatCompanion.clearAll failed:', e);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  // Floater anchor — RTL flips to the left side.
  const anchorEdge = dir === 'rtl' ? 'left-4 sm:left-6' : 'right-4 sm:right-6';

  return (
    <>
      {/* Trigger — quiet copper ∞ on a soft white circle */}
      {!open && (
        <button
          type="button"
          onClick={() => setOpen(true)}
          aria-label={t('chat.open')}
          className={`fixed bottom-5 ${anchorEdge} z-40 w-14 h-14 rounded-full bg-white shadow-[0_8px_28px_-12px_rgba(42,39,36,0.35)] ring-1 ring-[#2A2724]/15 hover:ring-[#B85228]/40 transition-all duration-300 flex items-center justify-center group`}
          data-testid="chat-trigger"
        >
          <InfinitySymbol className="w-7 h-5 opacity-70 group-hover:opacity-100 transition-opacity" />
        </button>
      )}

      {/* Panel */}
      {open && (
        <div
          className={`fixed inset-x-0 bottom-0 sm:inset-x-auto ${dir === 'rtl' ? 'sm:left-6' : 'sm:right-6'} sm:bottom-6 z-50 sm:w-[26rem] bg-white sm:rounded-2xl shadow-[0_20px_60px_-15px_rgba(42,39,36,0.45)] ring-1 ring-[#2A2724]/10 flex flex-col`}
          style={{
            direction: dir,
            // Use small/dynamic viewport height so iOS keyboard doesn't push the
            // composer off-screen. `svh` shrinks with the keyboard; on desktop
            // we cap at 34rem so the panel stays compact.
            height: 'min(100svh, 34rem)',
            maxHeight: '100svh',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
          }}
          data-testid="chat-panel"
        >
          {/* Header */}
          <header className="flex items-center justify-between px-5 py-3 border-b border-[#2A2724]/10">
            <div className="flex items-center gap-2.5">
              <InfinitySymbol className="w-6 h-4 opacity-80" />
              <span className="text-xs uppercase tracking-[0.18em] text-[#2A2724] font-semibold">
                {t('chat.title')}
              </span>
            </div>
            <div className="flex items-center gap-3">
              {history.length > 0 && (
                <button
                  type="button"
                  onClick={clearAll}
                  className="text-[0.7rem] uppercase tracking-[0.14em] text-[#2A2724]/50 hover:text-[#B85228] bg-transparent border-0 p-0 cursor-pointer transition-colors"
                  data-testid="chat-clear"
                >
                  {t('chat.clear')}
                </button>
              )}
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label={t('chat.close')}
                className="w-7 h-7 flex items-center justify-center text-[#2A2724]/60 hover:text-[#2A2724] bg-transparent border-0 cursor-pointer rounded-full hover:bg-[#2A2724]/5 transition-colors"
                data-testid="chat-close"
              >
                <CloseIcon className="w-4 h-4" />
              </button>
            </div>
          </header>

          {/* Scrollable history */}
          <div
            ref={scrollerRef}
            className="flex-1 overflow-y-auto px-5 py-5 space-y-4"
            data-testid="chat-history"
          >
            {history.length === 0 && !sending && (
              <p className="text-sm text-[#2A2724]/50 leading-relaxed text-center max-w-xs mx-auto py-8">
                {t('chat.empty')}
              </p>
            )}
            {history.map((m, i) => (
              <div
                key={m.id || `idx-${i}`}
                className={`flex ${m.role === 'user' ? (dir === 'rtl' ? 'justify-start' : 'justify-end') : (dir === 'rtl' ? 'justify-end' : 'justify-start')}`}
              >
                <div
                  className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${m.role === 'user' ? 'bg-[#2A2724] text-white' : 'bg-[#F5F1EC] text-[#2A2724]'}`}
                  data-testid={`chat-msg-${m.role}`}
                >
                  {m.content}
                </div>
              </div>
            ))}
            {sending && (
              <div className={`flex ${dir === 'rtl' ? 'justify-end' : 'justify-start'}`}>
                <div className="px-4 py-2.5 rounded-2xl bg-[#F5F1EC] text-[#2A2724]/50 text-sm">
                  <Dots />
                </div>
              </div>
            )}
            {error && (
              <p className="text-xs text-[#B85228] text-center" data-testid="chat-error">{error}</p>
            )}
          </div>

          {/* Composer */}
          <div className="border-t border-[#2A2724]/10 p-3">
            <div className="flex items-end gap-2">
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={onKeyDown}
                placeholder={t('chat.placeholder')}
                rows={1}
                maxLength={2000}
                enterKeyHint="send"
                autoCapitalize="sentences"
                autoCorrect="on"
                className="flex-1 resize-none border-0 bg-[#F5F1EC] rounded-xl px-4 py-2.5 text-base sm:text-sm text-[#2A2724] placeholder:text-[#2A2724]/40 focus:outline-none focus:ring-2 focus:ring-[#B85228]/30 leading-relaxed max-h-32"
                style={{ direction: dir }}
                data-testid="chat-input"
              />
              <button
                type="button"
                onClick={send}
                disabled={!input.trim() || sending}
                className="px-4 py-2.5 rounded-xl bg-[#2A2724] text-white text-xs uppercase tracking-[0.12em] font-semibold disabled:opacity-30 hover:bg-[#3A3431] transition-colors cursor-pointer disabled:cursor-not-allowed border-0"
                data-testid="chat-send"
              >
                {sending ? t('chat.sending') : t('chat.send')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function InfinitySymbol({ className = '' }) {
  return (
    <svg viewBox="0 0 120 60" className={className} fill="none" aria-hidden="true">
      <defs>
        <linearGradient id="cc-copper" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" stopColor="#5C1810" />
          <stop offset="50%" stopColor="#D87040" />
          <stop offset="100%" stopColor="#5C1810" />
        </linearGradient>
      </defs>
      <path
        d="M 60 30 C 50 0, 5 0, 5 30 C 5 60, 50 60, 60 30 C 70 0, 115 0, 115 30 C 115 60, 70 60, 60 30 Z"
        stroke="url(#cc-copper)"
        strokeWidth="4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon({ className = '' }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <line x1="6" y1="6" x2="18" y2="18" />
      <line x1="18" y1="6" x2="6" y2="18" />
    </svg>
  );
}

function Dots() {
  return (
    <span className="inline-flex items-center gap-1">
      <span className="w-1.5 h-1.5 rounded-full bg-[#2A2724]/40 animate-pulse" style={{ animationDelay: '0s' }} />
      <span className="w-1.5 h-1.5 rounded-full bg-[#2A2724]/40 animate-pulse" style={{ animationDelay: '0.15s' }} />
      <span className="w-1.5 h-1.5 rounded-full bg-[#2A2724]/40 animate-pulse" style={{ animationDelay: '0.3s' }} />
    </span>
  );
}
